import { useState, type ReactElement } from 'react';
import { useFeaturesPageViewModel } from '@/features/features/hooks/useFeaturesPageViewModel';
import { FeaturesTablePanel } from '@/features/features/pages/FeaturesTablePanel';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import {
  formatRangeWindowNoDataText,
  resolveQualityComparisonWindowLabel,
  useQualityDateRange,
} from '@/features/quality/hooks';
import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { MetricsPageToolbar } from '@/features/shared/components/metricsToolbar/MetricsPageToolbar';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { r, s } from '@/theme/tokens';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Box, FeaturesIcon, Text } from '@/ui/primitives';
import type { FeatureStatus } from '@flowqa/shared';

const resolveFeaturesStatusCardIcon = (statusId: string): typeof FeaturesIcon => {
  if (statusId === 'total') return FeaturesIcon;
  return resolveGenericStatusCardIcon(statusId);
};

export const FeaturesPage = (): ReactElement => {
  const {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    projectFilterOptions,
    showProjectFilter,
    onStatusToggle,
    onStatusesChange,
    onTagsChange,
    onProjectsChange,
    tableFilters,
    featureMetricCards,
    featureMetricCardsLoading,
  } = useFeaturesPageViewModel();
  const [searchValue, setSearchValue] = useState('');
  const { rangeStartDay, rangeEndDay } = useQualityDateRange(range);
  const comparisonWindowLabel = resolveQualityComparisonWindowLabel(range);
  const tableNoRowsText = formatRangeWindowNoDataText(
    getEmptyStateMessage('entity.noneFound', { entity: 'feature' }),
    range,
    false,
  );

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Features
        </Text>
      }
      headerActions={
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: r('card'),
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            display: 'flex',
            alignItems: 'center',
            px: s('md'),
            width: '100%',
          }}
        >
          <MetricsPageToolbar
            title=""
            showInlineFilters
            searchAriaLabel="Search features"
            searchValue={searchValue}
            onDebouncedSearchChange={setSearchValue}
            filterStatuses={selectedStatuses}
            statusOptions={featureStatusOptions}
            onFilterStatusesChange={(next) => {
              onStatusesChange(next as FeatureStatus[]);
            }}
            filterTags={selectedTags}
            onFilterTagsChange={onTagsChange}
            filterProjects={showProjectFilter ? selectedProjects : undefined}
            onFilterProjectsChange={showProjectFilter ? onProjectsChange : undefined}
            projectOptions={showProjectFilter ? projectFilterOptions : undefined}
            dateQuickFilter={range === 'all' ? '30d' : range}
            onDateQuickFilterChange={(next) => {
              setRange(next);
            }}
            fromDate={null}
            toDate={null}
            latestQueryStartDay={rangeStartDay}
            latestQueryEndDay={rangeEndDay}
            onFromDateChange={() => undefined}
            onToDateChange={() => undefined}
            availableTags={tagOptions.map((option) => option.value)}
            showDateControls
          />
        </Box>
      }
      summary={
        <StatusMetricCardsRow
          cards={featureMetricCards}
          loading={featureMetricCardsLoading}
          selectedIds={selectedStatuses}
          comparisonWindowLabel={comparisonWindowLabel}
          resolveStatusCardIcon={resolveFeaturesStatusCardIcon}
          onToggle={(status) => {
            if (status === 'total') return;
            onStatusToggle(status as FeatureStatus);
          }}
        />
      }
      content={
        <FeaturesTablePanel
          fixedFilters={tableFilters}
          rangeStartDay={rangeStartDay}
          rangeEndDay={rangeEndDay}
          noRowsText={tableNoRowsText}
          searchValue={searchValue}
        />
      }
      paddingBottom="lg"
    />
  );
};
